import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MultiSelect } from '../../../../../../../components/common';
import { IOuter } from './LabelsSelect.types';
import { convertLabels, dropLabel, includesLabel, reconvertLabels } from '../utils';
import { find, propEq } from 'ramda';
import { ILabel } from '../LabelsEditor.types';
import { useOnClickOutside } from '../../../../../../../utils/hooks/useOnClickOutside';

export const LabelsSelect: FC<IOuter> = ({
  labels: labelsFromProps,
  maxNumRows = 2,
  dataHook,
  onLabelAdd,
  onLabelRemove,
  onClickOutside,
  className,
}) => {
  const { formatMessage } = useIntl();
  const [value, setValue] = useState('');
  const handleChange = useCallback((event) => setValue(event.target.value), []);

  const labels = useMemo(() => convertLabels(labelsFromProps), [labelsFromProps]);

  const handleLabelAdd = useCallback(
    ([newLabel]) => {
      const newLabels = includesLabel(newLabel, labels) ? labels : [...labels, { label: newLabel, id: newLabel }];
      onLabelAdd(newLabel, reconvertLabels(newLabels));
    },
    [labels, onLabelAdd],
  );

  const handleLabelRemove = useCallback(
    (labelId) => {
      const labelObject = find<ILabel>(propEq('id', labelId), labels);

      if (!labelObject) {
        return;
      }

      return onLabelRemove(labelObject.label, reconvertLabels(dropLabel(labelId, labels)));
    },
    [labels, onLabelRemove],
  );

  const wrapperRef = useRef();
  useOnClickOutside(wrapperRef, onClickOutside);

  return (
    <div ref={wrapperRef} className={className}>
      <MultiSelect
        value={value}
        onChange={handleChange}
        tags={labels}
        onManuallyInput={handleLabelAdd}
        onRemoveTag={handleLabelRemove}
        maxNumRows={maxNumRows}
        placeholder={formatMessage({ id: 'labels-editor.add-some-labels-placeholder' })}
        dataHook={dataHook}
        size="small"
      />
    </div>
  );
};
